// Shared thermal colour gradient: cold → hot (dark-blue → blue → green → amber → red)
export const GRADIENT_STOPS: [number, [number, number, number]][] = [
    [0,    [30,  58, 138]],
    [0.25, [37,  99, 235]],
    [0.5,  [22, 163,  74]],
    [0.75, [202, 138,   4]],
    [1.0,  [220,  38,  38]],
];

export type FaceId = "PX" | "NX" | "PY" | "NY" | "PZ" | "NZ";

export const FACE_ORDER: FaceId[] = ["PX", "NX", "PY", "NY", "PZ", "NZ"];

export const FACE_CONFIGS: Record<FaceId, { rotation: [number, number, number]; offset: [number, number, number] }> = {
    PX: { rotation: [ Math.PI / 2, 0, 0], offset: [ 0,    -0.5,  0   ] },  // Bottom
    NX: { rotation: [-Math.PI / 2, 0, 0], offset: [ 0,     0.5,  0   ] },  // Top
    PY: { rotation: [0,  Math.PI / 2, 0], offset: [ 0.5,   0,    0   ] },  // Right
    NY: { rotation: [0, -Math.PI / 2, 0], offset: [-0.5,   0,    0   ] },  // Left
    PZ: { rotation: [0,  0,           0], offset: [ 0,     0,    0.5 ] },  // Front
    NZ: { rotation: [0,  Math.PI,     0], offset: [ 0,     0,   -0.5 ] },  // Back
};

export const LEGEND_BG = `linear-gradient(to bottom, ${GRADIENT_STOPS.slice().reverse().map(([, c]) => `rgb(${c.join(",")})`).join(",")})`;

export const AXES: { dir: [number, number, number]; color: string; label: string; annotation: string }[] = [
    { dir: [0, -1, 0], color: "#FF3333", label: "X", annotation: "Yaw" },
    { dir: [1,  0, 0], color: "#CCF54E", label: "Y", annotation: "Roll" },
    { dir: [0,  0, 1], color: "#47B7FF", label: "Z", annotation: "Pitch" },
];
export const SHAFT_LEN = 1.3;
export const HEAD_LEN  = 0.09;
export const HEAD_RAD  = 0.016;
export const SHAFT_RAD = 0.005;

export function tempToRgb01(temp: number, min: number, max: number): [number, number, number] {
    const t = max === min ? 0.5 : Math.max(0, Math.min(1, (temp - min) / (max - min)));
    const idx = Math.max(1, GRADIENT_STOPS.findIndex(([s]) => s >= t));
    const [s0, c0] = GRADIENT_STOPS[idx - 1];
    const [s1, c1] = GRADIENT_STOPS[idx];
    const u = (t - s0) / (s1 - s0);
    return [
        (c0[0] + (c1[0] - c0[0]) * u) / 255,
        (c0[1] + (c1[1] - c0[1]) * u) / 255,
        (c0[2] + (c1[2] - c0[2]) * u) / 255,
    ];
}

export const tempToHex = (temp: number, min: number, max: number): string =>
    `#${tempToRgb01(temp, min, max).map((v) => Math.round(v * 255).toString(16).padStart(2, "0")).join("")}`;

export const EMPTY_NODES: Record<string, number> = {};

export const THERMAL_UPDATE_INTERVAL_MS = 3000;

export const INT_ORDER = ["INT1", "INT2"] as const;
export type IntDeckId = typeof INT_ORDER[number];

export const INT_LABELS: Record<IntDeckId, string> = {
    INT1: "Deck 1",
    INT2: "Deck 2"
};

export const FACE_LABELS: Record<FaceId, string> = {
    PX: "+X Face",
    NX: "-X Face",
    PY: "+Y Face",
    NY: "-Y Face",
    PZ: "+Z Face",
    NZ: "-Z Face",
};

export const JSON_FACE_KEY_TO_ID: Record<string, FaceId> = {
    X: "PX",
    "-X": "NX",
    Y: "PY",
    "-Y": "NY",
    Z: "PZ",
    "-Z": "NZ",
};

export const INT_KEY_MAP = [
    { api: "Deck 1", internal: "INT1" },
    { api: "Deck 2", internal: "INT2" },
] as const;

export const PLAYBACK_SPEEDS = [0.25, 0.5, 1, 2, 5, 10];

export const LEGEND_2D_BG = `linear-gradient(to bottom, ${GRADIENT_STOPS.slice().reverse().map(([, c]) => `rgb(${c.join(",")})`).join(",")})`;

export const PANEL_TO_FACE_ID: Record<string, string> = {
    "X":  "PX",
    "-X": "NX",
    "Y":  "PY",
    "-Y": "NY",
    "Z":  "PZ",
    "-Z": "NZ",
    "Deck 1": "INT1",
    "Deck 2": "INT2",
};

export const ALL_FACE_LABEL_MAP: Record<string, string> = { ...FACE_LABELS, ...INT_LABELS };

export const DESIGNER_PANEL_LABEL_TO_FACE_ID: Record<string, string> = {
    ...PANEL_TO_FACE_ID,
    "+X": "PX",
    "-X": "NX",
    "+Y": "PY",
    "-Y": "NY",
    "+Z": "PZ",
    "-Z": "NZ",
};

export const FACE_ID_TO_DECK_MAP: Record<string, string> = {
    PX: "deck-0",
    NX: "deck-3",
    INT1: "deck-1",
    INT2: "deck-2",
};
